import * as React from "react";
import * as _ from "lodash";
import * as moment from "moment";
import * as yup from "yup";
import {DateInput} from "../components/inputs/DateInput";
import {PluginTemplates} from "@ferry/plugin-ferry-journey";
import {lazyInject} from "@athena-portal/core";
import {IFalconPropositionEngine, Types, VEHICLE_REG_NUMBER_REGEX, SURNAME_REGEX, POSTCODE_REGEX} from "@ferry/journey-services";
import {resourcesHelper} from "@ferry/portal-core";
import {Navigate} from "react-router";
import {Overlay} from "@athena-portal/components";

const FormGroup: React.FC<React.HTMLProps<HTMLDivElement>> = (props) => (
    <div {...props} className="form-group"/>
)

const Label: React.FC<React.HTMLProps<HTMLLabelElement>> = (props) => (
    <label {...props} className=""/>
)

const InputGroup: React.FC<React.HTMLProps<HTMLDivElement>> = (props) => (
    <div {...props} className="input-group"/>
)

const Input: React.FC<{ isValid?: boolean } & React.HTMLProps<HTMLInputElement>> = (props) => (
    <input
        {..._.omit(props, "isValid")}
        className={`form-control ${props.isValid === false ? "is-invalid" : ""}
        ${props.className}`}
    />
)

const ErrorMessage: React.FC<React.HTMLProps<HTMLDivElement>> = (props) => (
    <div {...props} className="invalid-feedback d-block"/>
)

interface IProps {
    location?: any
    match?: any
}

interface Form {
    hasBeenSubmitted: boolean
    surname: string
    dob: string
    postcode: string
    crn: string
}

interface IState {
    form: Form
    formErrors: { [key: string]: string }
    interaction?: any;
    journey?: any;
    match?: any;
    loadingInteraction?: boolean;
    loadingJourney?: boolean;
    interactionFound?: boolean;
}

export class RenewalQuotePage extends React.Component<IProps, IState> {

    private readonly formSchema: any;

    private readonly pluginTemplates = new PluginTemplates();

    @lazyInject(Types.FalconPropositionEngine)
    private readonly propositionEngine: IFalconPropositionEngine;

    constructor(props: IProps) {
        super(props);

        const requiredValidation = {
            conditionField: "hasBeenSubmitted",
            conditionOptions: {
                is: true,
                then: yup.string().required("This field is mandatory")
            }
        }
        this.formSchema = yup.object({
            hasBeenSubmitted: yup.boolean(),
            surname: yup
                .string()
                .when(requiredValidation.conditionField, requiredValidation.conditionOptions)
                .matches(
                    SURNAME_REGEX,{
                        message: "Last name must be between 2–60 characters in length. Apostrophes, hyphens or spaces can also be used, but not in consecutive order",
                        excludeEmptyString: true,
                    }),
            dob: yup
                .string()
                .when(requiredValidation.conditionField, requiredValidation.conditionOptions)
                .test("customDate format", "Invalid date format. Example: 06/02/1981", (dob: string) =>
                    !dob || dob.length === 10 && moment(dob, "yyyy-MM-DD", true).isValid()
                )
                .test("customDate not in future", "Date can't be in the future", (dob: string) =>
                    !dob || moment(dob).isSameOrBefore(moment())
                )
                .test("customDate year minimum", "Year can't be less than 1900", (dob: string) =>
                    !dob || moment(dob).year() >= 1900
                ),
            crn: yup
                .string()
                .when(requiredValidation.conditionField, requiredValidation.conditionOptions)
                .matches(VEHICLE_REG_NUMBER_REGEX, {
                    message: "Please adjust your registration number",
                    excludeEmptyString: true
                }),
            postcode: yup
                .string()
                .when(requiredValidation.conditionField, requiredValidation.conditionOptions)
                .matches(POSTCODE_REGEX, {
                    message: "Please adjust post code",
                    excludeEmptyString: true
                }),
        });

        this.state = {
            form: {
                hasBeenSubmitted: false,
                surname: "",
                dob: "",
                postcode: "",
                crn: ""
            },
            formErrors: {}
        };
    }

    public componentDidMount() {
        this.pluginTemplates.setPageTitle("Renewal quote");

        window.scrollTo(0, 0);

        const url = new URL(window.location.href);
        const surname = url.searchParams.get("surname");
        if (surname) {
            const form = {...this.state.form};
            form.surname = surname;
            this.setState({form});
        }
    }

    public render() {

        if (this.state.loadingInteraction) {
            return <Overlay show={true}/>
        }

        if (this.state.interactionFound && this.state.loadingInteraction === false) {
            return (
                <Navigate
                    to={"/journey/show/product/" + this.state.interaction.product + "/process/nb/summary/" + this.state.interaction._id}/>
            );
        }
        const {formErrors} = this.state;
        return (
            <React.Fragment>
                {this.pluginTemplates.renderHeader()}
                <div className="main-plugin renewal-quote-page">

                    {this.state.interactionFound === false && <div className="page-validation fixed" role="alert">
                        <div className="page-validation-alert">
                            <img className="page-validation-alert-icon" alt="Alert icon"
                                 src={`/${resourcesHelper.THEME_PATH}/img/alert-red.svg`}/>
                        </div>
                        <div className="page-validation-message">
                            <p className="page-validation-message-top">
                                Quote not found, please enter details again...
                            </p>
                        </div>
                    </div>}

                    <>
                        <div className="imageBanner">
                            <img src={`/${resourcesHelper.THEME_PATH}/img/details_logo.png`}
                                 alt={`${resourcesHelper.THEME} details logo`}
                            />
                        </div>
                    </>

                    <div className="text-group title plugin">
                        <h1>
                            Find your renewal quote
                        </h1>
                    </div>
                    <div className="renewal-quote-container plugin">
                        <form onSubmit={this.handleSubmit}>
                            <div className="inner-content">
                                <FormGroup>
                                    <Label htmlFor="surname">Surname</Label>
                                    <InputGroup>
                                        <Input
                                            type="text"
                                            id="surname"
                                            name="surname"
                                            value={this.state.form.surname}
                                            isValid={!formErrors.surname}
                                            onChange={this.handleChange}
                                        />
                                        {formErrors.surname && <ErrorMessage>{formErrors.surname}</ErrorMessage>}
                                    </InputGroup>
                                </FormGroup>
                                <FormGroup>
                                    <Label htmlFor="dob">Date of birth</Label>
                                    <InputGroup>
                                        <DateInput
                                            id="dob"
                                            name="dob"
                                            value={this.state.form.dob}
                                            isValid={!formErrors.dob}
                                            onChange={this.handleChange}
                                        />
                                        {formErrors.dob && <ErrorMessage>{formErrors.dob}</ErrorMessage>}
                                    </InputGroup>
                                </FormGroup>
                                <FormGroup>
                                    <Label htmlFor="postcode">Postcode</Label>
                                    <InputGroup>
                                        <Input
                                            type="text"
                                            id="postcode"
                                            name="postcode"
                                            value={this.state.form.postcode}
                                            isValid={!formErrors.postcode}
                                            onChange={this.handleChange}
                                            className="input-data"
                                        />
                                        {formErrors.postcode && <ErrorMessage>{formErrors.postcode}</ErrorMessage>}
                                    </InputGroup>
                                </FormGroup>
                                <FormGroup>
                                    <Label htmlFor="crn">Car registration number</Label>
                                    <InputGroup>
                                        <Input
                                            type="text"
                                            id="crn"
                                            name="crn"
                                            value={this.state.form.crn}
                                            isValid={!formErrors.crn}
                                            onChange={this.handleChange}
                                            className="input-data"
                                        />
                                        {formErrors.crn && <ErrorMessage>{formErrors.crn}</ErrorMessage>}
                                    </InputGroup>
                                </FormGroup>
                            </div>
                            <button
                                type="submit"
                                id="renewalQuote"
                                className={`btn btn-primary ${this.isFormValid() ? "" : "btn-disabled"} btn-next plugin`}
                            >Next
                            </button>
                        </form>
                    </div>
                </div>
                {this.pluginTemplates.renderFooter()}
            </React.Fragment>
        );
    }

    private handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = {...this.state.form};
        form.hasBeenSubmitted = true;
        this.setState(
            {form},
            async () => {
                await this.validateForm();
                if (this.isFormValid()) {
                    this.setState({loadingInteraction: true});
                    const quoteStatus = "MIGRATED_RENEWAL";

                    this.propositionEngine.fetchInteractionForSurnameAndDateOfBirthAndCarRegAndPostcodeAndStatus(
                        this.state.form.surname,
                        this.state.form.dob,
                        this.state.form.crn,
                        this.state.form.postcode,
                        quoteStatus).subscribe(
                        (interaction: any) => {
                            if (interaction) {
                                this.setState({interaction, loadingInteraction: false, interactionFound: true});
                            } else {
                                this.setState({loadingInteraction: false, interactionFound: false});
                            }
                        }
                    );
                }
            }
        );
    }

    private handleChange = (event: React.FormEvent<HTMLInputElement>) => {
        const form = {...this.state.form};
        const target = event.target as HTMLInputElement;
        // @ts-ignore
        form[target.name] = target.value;
        this.setState({form}, this.validateForm);
    }

    private isFormValid = (): boolean => {
        return _.isEmpty(this.state.formErrors);
    }

    private validateForm = () => {
        return this.formSchema
            .validate(this.state.form, {abortEarly: false})
            .then(() => this.setState({formErrors: {}}))
            .catch((caughtErrors: any) => {
                const formErrors: Record<string, string> = {};
                caughtErrors.inner.reverse().forEach((innerError: any) => {
                    formErrors[innerError.path] = innerError.message
                })
                this.setState({formErrors})
            });
    }
}
